import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, ButtonBase } from "@mui/material";

// Segmented control whose white pill slides under the chosen option
export function SwapTabs({ value, onChange, options }) {
  const index = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <Box role="tablist" sx={{ position: "relative", display: "grid", gridTemplateColumns: `repeat(${options.length}, 1fr)`, p: 0.5, mb: 1.25, bgcolor: "#f6f1e4", borderRadius: "14px" }}>
      <Box
        sx={{
          position: "absolute", top: 4, bottom: 4, left: 4, borderRadius: "10px", bgcolor: "#fff",
          width: `calc((100% - 8px) / ${options.length})`,
          boxShadow: "0 3px 10px rgba(138,106,36,0.22)",
          transform: `translateX(${index * 100}%)`,
          transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
      {options.map((o) => (
        <ButtonBase
          key={o.value}
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          sx={{ position: "relative", zIndex: 1, py: 0.8, gap: 0.75, borderRadius: "10px", fontSize: "0.85rem", fontWeight: 600, color: o.value === value ? "#8a6a24" : "#7a6a45", transition: "color 0.3s ease" }}
        >
          {o.icon}
          {o.label}
        </ButtonBase>
      ))}
    </Box>
  );
}

// Two or more sections in the same spot: the one you pick fades in while the other fades out, and the
// height eases to the visible section. Gentle on purpose: no sliding track.
export function SwapPanes({ index, children }) {
  const refs = useRef([]);
  const [height, setHeight] = useState(null);

  useLayoutEffect(() => {
    const el = refs.current[index];
    if (!el) return undefined;
    const update = () => setHeight(el.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [index]);

  return (
    <Box
      sx={{
        display: "grid",
        mx: -0.5, px: 0.5, py: 0.5,
        boxSizing: "border-box",
        height: height === null ? "auto" : height + 8,
        overflow: "hidden",
        transition: "height 0.55s cubic-bezier(0.4, 0, 0.2, 1)",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      {children.map((child, i) => {
        const active = i === index;
        return (
          <Box
            key={i}
            aria-hidden={!active}
            sx={{
              gridArea: "1 / 1",
              alignSelf: "start",
              opacity: active ? 1 : 0,
              transform: active ? "translateY(0) scale(1)" : "translateY(10px) scale(0.985)",
              filter: active ? "blur(0)" : "blur(3px)",
              visibility: active ? "visible" : "hidden",
              pointerEvents: active ? "auto" : "none",
              // the new section waits a moment so the two never overlap harshly
              transition: active
                ? "opacity 0.5s ease 0.12s, transform 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.12s, filter 0.5s ease 0.12s, visibility 0s"
                : "opacity 0.3s ease, transform 0.3s ease, filter 0.3s ease, visibility 0s 0.3s",
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            <Box ref={(el) => { refs.current[i] = el; }} sx={{ display: "flow-root" }}>{child}</Box>
          </Box>
        );
      })}
    </Box>
  );
}
